import {
  averageOf,
  inclusiveDayCount,
  isWithinPeriod,
  percentChange,
  toFiniteNumber,
} from './causalNumbers'

/**
 * The module's correctness core: `causal_output` → `CausalEffect[]`.
 *
 * Ported 1:1 from v3 `ResultsView.vue`'s `campaigns`/`results` computeds (the derivation math,
 * every branch, its sign conventions and its div-by-zero guards), minus three classes of v3
 * behaviour that do not belong in a model:
 *
 * 1. **Copy and formatting.** v3 baked English sentences, `toFixed` strings, hex colours and
 *    Font Awesome class names into every row. Here a verdict is an i18n key + named params +
 *    tone, values stay raw numbers, and the metric catalog is reached only through the injected
 *    {@link MetricCatalog} accessors — so this file imports no Vue, no store and no `@/features/*`.
 * 2. **Blind property access.** v3 trusts the payload (`info.new_budget`, `summary.pre[m]`,
 *    `new Date(postPeriod[0])`). The payload is unversioned (BE-13-03 is not shipped), so every
 *    read here is validated and every arithmetic input is coerced.
 * 3. **`NaN` propagation.** `budget_change_pct * 100 || null`, `Math.round(NaN) + 1` and
 *    `sum + (d[m] || 0)` over string values all reached the UI in v3.
 */

/** Values are read defensively: this is a wire blob, not a typed model. */
export type CausalOutput = Readonly<Record<string, unknown>>

/** One `results[]` entry of a run-results page (`RunResultsPage`), narrowed to what is derived. */
export interface CausalProfileResultLike {
  full_result?: { causal_output?: CausalOutput | null } | null
}

/** One loaded page of `GET .../runs/{id}/results/`. */
export interface CausalResultPageLike {
  results?: readonly (CausalProfileResultLike | null | undefined)[] | null
}

/**
 * The metric catalog seam (contract 13 §9, §16).
 *
 * The mapper never reads the reports feature or a store: labels, direction and column
 * descriptors arrive as functions. `TColumn` stays generic so `getColumn` can return a real
 * `ReportColumn` at the call site without this file importing one.
 */
export interface MetricCatalog<TColumn = unknown> {
  /** Column descriptor forwarded to the view for value formatting; never inspected here. */
  getColumn: (metricKey: string) => TColumn | null
  /** Display label for a metric key. */
  metricLabel: (metricKey: string) => string
  /** `true` lower-is-better, `false` higher-is-better, `null` direction unknown. */
  lowerIsBetter: (metricKey: string) => boolean | null
}

export interface MetricDelta {
  preAvg: number
  postAvg: number
  /** Percent change of the post window against the pre window; `0` when the baseline is 0. */
  delta: number
}

export type CausalTimeseriesPoint = Readonly<Record<string, unknown>>

/** `[start, end]` ISO dates; `null` when the payload has no usable window. */
export type DatePeriod = readonly [string, string] | null

export type VerdictTone = 'positive' | 'negative' | 'neutral'

/** Verdict copy lives in the feature i18n bundle; the mapper only decides which key applies. */
export const VERDICT_KEYS = {
  added: 'impactAnalysis.results.verdict.added',
  cost: 'impactAnalysis.results.verdict.cost',
  saved: 'impactAnalysis.results.verdict.saved',
  increased: 'impactAnalysis.results.verdict.increased',
  unclear: 'impactAnalysis.results.verdict.unclear',
} as const

export type VerdictKey = (typeof VERDICT_KEYS)[keyof typeof VERDICT_KEYS]

/** Rendered by the view when {@link CausalEffect.name} is `null`. */
export const UNKNOWN_ENTITY_KEY = 'impactAnalysis.results.unknownEntity'

export interface VerdictParams {
  /** Absolute cumulative effect, unformatted: the view formats it with `targetColumn`. */
  magnitude: number
  /** Target metric label from the catalog; `''` when the run declares no target metric. */
  metric: string
  /** Inclusive length of the post window; `null` when the payload has no usable window. */
  days: number | null
}

export interface CausalVerdict {
  key: VerdictKey
  tone: VerdictTone
  params: VerdictParams
}

export interface CausalEffect<TColumn = unknown> {
  id: string
  /** `null` when nothing in the payload names the entity — the view falls back to an i18n key. */
  name: string | null
  changeDate: string | null
  prePeriod: DatePeriod
  postPeriod: DatePeriod
  isSignificant: boolean
  /** `model_metrics.effect_average_abs`. */
  effectAvgAbs: number
  /** `causal_summary_{id}.counterfactual.real_impact` when present, else `model_metrics.effect_cumulative_abs`
   *  — the "real impact" the table sorts and renders. */
  upliftPct: number
  absUplift: number
  /** Whether the backend's own `counterfactual.real_impact` was present — gates the drawer's whole
   *  impact-verdict card. A run can carry a `counterfactual` block with no `real_impact` in it, or
   *  none at all; either way, `upliftPct`'s `model_metrics` fallback isn't a substitute worth showing. */
  hasRealImpactData: boolean
  targetMetric: string | null
  targetColumn: TColumn | null
  targetPostAvg: number
  /** What the target metric would have been without the change: `targetPostAvg − effectAvgAbs`. */
  counterfactual: number
  controlMetrics: readonly string[]
  deltas: Readonly<Record<string, MetricDelta>>
  hasBudgetChange: boolean
  newBudget: number | null
  oldBudget: number | null
  budgetChangePct: number | null
  hasMetricChange: boolean
  newValue: number | null
  oldValue: number | null
  valueChangePct: number | null
  timeseries: readonly CausalTimeseriesPoint[]
  /** `causal_summary_{id}.overall[metric]` — the backend's own longer-window, attribution-adjusted
   *  aggregate. Never derived from the loaded timeseries pages (those may not cover the same
   *  window the backend aggregated over), and `null` when the payload carries none. */
  overallTotals: Readonly<Record<string, number>> | null
  /** Backend-authored narrative (`causal_summary_text_*`), not frontend copy. */
  insightText: string
  verdict: CausalVerdict
}

/** v3's unanchored key probe: `__segment_ctx__causal_info_x` resolves to the same id `x`. */
const ENTITY_KEY_PATTERN = /causal_(?:info|structured)_(.+)$/u
const CAMPAIGN_ID_PREFIX = 'campaign_id_'
const EMPTY_RECORD: CausalOutput = Object.freeze({})

function record(value: unknown): CausalOutput {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as CausalOutput)
    : EMPTY_RECORD
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null
}

/**
 * Backend boolean flag. v3 read these truthily, which also accepts the string `'false'`; the
 * accepted spellings are pinned instead so a stringified flag cannot invert a verdict.
 */
function flag(value: unknown): boolean {
  return value === true || value === 1 || value === 'true'
}

function metricKeys(value: unknown): string[] {
  return Array.isArray(value) ? value.flatMap((entry) => text(entry) ?? []) : []
}

function period(value: unknown): DatePeriod {
  if (!Array.isArray(value)) return null
  const start = text(value[0])
  const end = text(value[1])
  return start !== null && end !== null ? [start, end] : null
}

function rowsOf(value: unknown): CausalTimeseriesPoint[] {
  const data = record(value).data
  return Array.isArray(data) ? data.map(record) : []
}

function causalOutputOf(page: CausalResultPageLike | null | undefined): CausalOutput {
  return record(page?.results?.[0]?.full_result?.causal_output)
}

function pageList(
  pages: readonly CausalResultPageLike[] | null | undefined,
): CausalResultPageLike[] {
  return Array.isArray(pages) ? pages.filter((page) => page != null) : []
}

/**
 * Every entity id in the loaded pages, from the `causal_(info|structured)_{id}` keys and merged
 * across **all** pages — later pages add entities, they never replace the earlier ones.
 */
export function listEntityIds(pages: readonly CausalResultPageLike[] | null | undefined): string[] {
  const ids = new Set<string>()
  for (const page of pageList(pages)) {
    for (const key of Object.keys(causalOutputOf(page))) {
      const id = ENTITY_KEY_PATTERN.exec(key)?.[1]
      if (id) ids.add(id)
    }
  }
  return [...ids]
}

/** Flattens the loaded pages into one `causal_output`; later pages win per key, as in v3. */
export function mergeCausalOutput(
  pages: readonly CausalResultPageLike[] | null | undefined,
): CausalOutput {
  const merged: Record<string, unknown> = {}
  for (const page of pageList(pages)) Object.assign(merged, causalOutputOf(page))
  return merged
}

/** `info.campaign_name` → segment ctx `campaign_name`/`name` → id minus its `campaign_id_` prefix. */
function resolveName(info: CausalOutput, context: CausalOutput, id: string): string | null {
  const direct = text(info.campaign_name) ?? text(context.campaign_name) ?? text(context.name)
  if (direct !== null) return direct
  return text(id.startsWith(CAMPAIGN_ID_PREFIX) ? id.slice(CAMPAIGN_ID_PREFIX.length) : id)
}

function resolveDeltas(
  metrics: readonly string[],
  timeseries: readonly CausalTimeseriesPoint[],
  prePeriod: DatePeriod,
  postPeriod: DatePeriod,
): Record<string, MetricDelta> {
  const preRows = timeseries.filter((row) => isWithinPeriod(row.date, prePeriod))
  const postRows = timeseries.filter((row) => isWithinPeriod(row.date, postPeriod))
  const deltas: Record<string, MetricDelta> = {}
  for (const metric of metrics) {
    const preAvg = averageOf(preRows, metric)
    const postAvg = averageOf(postRows, metric)
    deltas[metric] = { preAvg, postAvg, delta: percentChange(preAvg, postAvg) }
  }
  return deltas
}

function resolveVerdict(
  upliftPct: number,
  isSignificant: boolean,
  targetMetric: string | null,
  postPeriod: DatePeriod,
  catalog: MetricCatalog<unknown>,
): CausalVerdict {
  const params: VerdictParams = {
    magnitude: Math.abs(upliftPct),
    metric: targetMetric === null ? '' : catalog.metricLabel(targetMetric),
    days: inclusiveDayCount(postPeriod?.[0], postPeriod?.[1]),
  }
  if (!isSignificant) return { key: VERDICT_KEYS.unclear, tone: 'neutral', params }

  const isInverse = targetMetric !== null && catalog.lowerIsBetter(targetMetric) === true
  if (!isInverse) {
    return upliftPct > 0
      ? { key: VERDICT_KEYS.added, tone: 'positive', params }
      : { key: VERDICT_KEYS.cost, tone: 'negative', params }
  }
  return upliftPct < 0
    ? { key: VERDICT_KEYS.saved, tone: 'positive', params }
    : { key: VERDICT_KEYS.increased, tone: 'negative', params }
}

function deriveEffect<TColumn>(
  id: string,
  output: CausalOutput,
  catalog: MetricCatalog<TColumn>,
): CausalEffect<TColumn> {
  const info = record(output[`causal_info_${id}`])
  const structured = record(output[`causal_structured_${id}`])
  const context = record(output[`__segment_ctx__causal_info_${id}`])
  const modelMetrics = record(structured.model_metrics)
  const parameters = record(structured.parameters)
  const timeseries = rowsOf(output[`causal_timeseries_${id}`])

  const targetMetric = text(parameters.target_metric)
  const controlMetrics = metricKeys(parameters.control_metrics)
  const prePeriod = period(info.pre_period)
  const postPeriod = period(info.post_period)

  const deltas = resolveDeltas(
    [...new Set(targetMetric === null ? controlMetrics : [targetMetric, ...controlMetrics])],
    timeseries,
    prePeriod,
    postPeriod,
  )
  const clientPostAvg = targetMetric === null ? 0 : (deltas[targetMetric]?.postAvg ?? 0)

  const summary = record(output[`causal_summary_${id}`])
  const counterfactual = record(summary.counterfactual)
  // `real_impact` is the window-aggregate signal the backend itself computes off the same
  // underlying data (`unit: "window_aggregate"`) — read directly rather than re-derived from
  // `model_metrics`, which can drift from it. Its presence (not just the `counterfactual` object's)
  // gates the drawer's whole impact-verdict card: a run can carry `counterfactual` with no
  // `real_impact` in it.
  const backendRealImpact = toFiniteNumber(counterfactual.real_impact)
  const hasRealImpactData = backendRealImpact !== null
  const backendWhatHappened = toFiniteNumber(counterfactual.what_happened)
  const backendWithoutChange = toFiniteNumber(counterfactual.without_change)
  const backendYourImpact = toFiniteNumber(counterfactual.your_impact)

  const effectAvgAbs = backendYourImpact ?? toFiniteNumber(modelMetrics.effect_average_abs) ?? 0
  const upliftPct = backendRealImpact ?? toFiniteNumber(modelMetrics.effect_cumulative_abs) ?? 0
  const targetPostAvg = backendWhatHappened ?? clientPostAvg
  const isSignificant =
    flag(modelMetrics.significant) || flag(record(structured.significance).significant)

  const newBudget = toFiniteNumber(info.new_budget)
  const oldBudget = toFiniteNumber(info.old_budget)
  const hasBudgetChange = newBudget !== null && oldBudget !== null
  const budgetChangePctRaw = toFiniteNumber(info.budget_change_pct)
  const newValue = toFiniteNumber(info.new_value)
  const oldValue = toFiniteNumber(info.old_value)
  const hasValues = newValue !== null && oldValue !== null

  const overall = record(summary.overall)
  const overallMetrics = [
    ...new Set(targetMetric === null ? controlMetrics : [targetMetric, ...controlMetrics]),
  ]
  const overallTotals =
    Object.keys(overall).length > 0
      ? Object.fromEntries(
          overallMetrics.map((metric) => [metric, toFiniteNumber(overall[metric]) ?? 0]),
        )
      : null

  return {
    id,
    name: resolveName(info, context, id),
    changeDate: text(info.change_date) ?? text(info.intervention_date),
    prePeriod,
    postPeriod,
    isSignificant,
    effectAvgAbs,
    upliftPct,
    absUplift: Math.abs(upliftPct),
    hasRealImpactData,
    targetMetric,
    targetColumn: targetMetric === null ? null : catalog.getColumn(targetMetric),
    targetPostAvg,
    counterfactual: backendWithoutChange ?? targetPostAvg - effectAvgAbs,
    controlMetrics,
    deltas,
    hasBudgetChange,
    newBudget,
    oldBudget,
    budgetChangePct: budgetChangePctRaw === null ? null : budgetChangePctRaw * 100,
    hasMetricChange: !hasBudgetChange && hasValues,
    newValue,
    oldValue,
    valueChangePct:
      hasValues && oldValue !== 0 ? ((newValue - oldValue) / Math.abs(oldValue)) * 100 : null,
    timeseries,
    overallTotals,
    insightText: text(record(output[`causal_summary_text_${id}`]).data) ?? '',
    verdict: resolveVerdict(upliftPct, isSignificant, targetMetric, postPeriod, catalog),
  }
}

/**
 * Two-group ordering (v3 §5): proven impact first by descending magnitude, then the not-clear
 * rows by ascending signed uplift so the worst suspected drops surface at the top.
 */
export function sortEffects<TColumn>(
  effects: readonly CausalEffect<TColumn>[],
): CausalEffect<TColumn>[] {
  const realImpact = effects
    .filter((effect) => effect.isSignificant)
    .sort((left, right) => right.absUplift - left.absUplift)
  const notClearYet = effects
    .filter((effect) => !effect.isSignificant)
    .sort((left, right) => left.upliftPct - right.upliftPct)
  return [...realImpact, ...notClearYet]
}

/** The whole derivation: loaded pages + metric catalog → ordered `CausalEffect[]`. */
export function mapCausalOutput<TColumn>(
  pages: readonly CausalResultPageLike[] | null | undefined,
  catalog: MetricCatalog<TColumn>,
): CausalEffect<TColumn>[] {
  const output = mergeCausalOutput(pages)
  const effects = listEntityIds(pages).map((id) => deriveEffect(id, output, catalog))
  return sortEffects(effects)
}

export const causalOutputMapper = {
  listEntityIds,
  mergeCausalOutput,
  mapCausalOutput,
  sortEffects,
}
